import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';

interface LayoutProps {
  children?: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = 'sidebarCollapsed';
const MOBILE_BREAKPOINT = 1024;

const REPORTS_ALLOWED_PREFIXES = ['/reports', '/admin/audit', '/admin/settings/audit', '/profile'];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Reports-only users cannot access clinical modules
  useEffect(() => {
    if (user?.role !== 'REPORTS') return;
    const path = location.pathname;
    const allowed = REPORTS_ALLOWED_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
    if (!allowed) {
      navigate('/reports/doctors', { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  // Initialize sidebar state from storage or default to collapsed
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    
    try {
      const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  // Persist sidebar preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(isCollapsed));
    } catch (error) {
      console.warn('Failed to save sidebar preference:', error);
    }
  }, [isCollapsed]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      console.log('Layout resize:', { 
        width: window.innerWidth, 
        mobile, 
        isMobileMenuOpen, 
        isCollapsed 
      });
      setIsMobile(mobile);
      
      // Auto-close mobile menu when switching to desktop
      if (!mobile && isMobileMenuOpen) {
        console.log('Auto-closing mobile menu due to desktop resize');
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Close mobile menu on Escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Memoized handlers for better performance
  const handleToggleSidebar = useCallback(() => {
    console.log('Toggle sidebar clicked:', { isMobile, isMobileMenuOpen, isCollapsed });
    if (isMobile) {
      setIsMobileMenuOpen(prev => {
        console.log('Mobile menu toggle:', prev, '->', !prev);
        return !prev;
      });
    } else {
      setIsCollapsed(prev => {
        console.log('Desktop sidebar toggle:', prev, '->', !prev);
        return !prev;
      });
    }
  }, [isMobile, isMobileMenuOpen, isCollapsed]);

  const handleCloseMobileMenu = useCallback(() => {
    console.log('Closing mobile menu');
    setIsMobileMenuOpen(false);
  }, []);


  return (
    <div className="h-screen bg-gray-50 flex flex-col lg:flex-row overflow-hidden">
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={handleCloseMobileMenu}
          role="button"
          tabIndex={-1}
          aria-label="Close navigation menu"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleCloseMobileMenu();
            }
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-[100] lg:z-[100]
          transform transition-transform duration-300 ease-in-out lg:transform-none
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-label="Main navigation"
        aria-hidden={!isMobileMenuOpen && isMobile ? 'true' : 'false'}
      >
        <Sidebar 
          isCollapsed={isCollapsed} 
          onToggleSidebar={handleToggleSidebar}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={handleCloseMobileMenu}
        />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <header className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 z-10">
          <Header 
            isCollapsed={isCollapsed} 
            onToggleSidebar={handleToggleSidebar}
            isMobileMenuOpen={isMobileMenuOpen}
          />
        </header>

        <main 
          className="flex-1 overflow-y-auto bg-gray-50 focus:outline-none scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-transparent"
          role="main"
          aria-label="Main content"
          tabIndex={-1}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6 min-h-full">
            {children || <Outlet />}
          </div>
        </main>

        {/* Footer */}
        <footer 
          className="flex-shrink-0 bg-white border-t border-gray-200 py-6 px-4 sm:px-6 lg:px-8"
          role="contentinfo"
        >
          <div className="container mx-auto">
            {/* Primary Footer Content */}
            <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600 gap-4">
              <div className="flex items-center gap-1 text-gray-500">
                <span>Powered by</span>
                <span className="font-semibold text-blue-600">K2X Technology</span>
              </div>
              
              {/* Version Info */}
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>Version 2.1.0</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;